import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Collapse, Row, Col, Select, Empty, Spin, Typography } from 'antd';
import { HeartOutlined } from '@ant-design/icons';
import { useTheme } from 'styled-components';
import { BaseChart, getChartColors } from '@app/components/common/charts/BaseChart';
import { getUserHealthData } from '@app/api/admin.api';
import {
  HealthAdminResponse,
  HealthMetricSeries,
  metricLabel,
  metricKind,
  metricColor,
} from '@app/models/HealthDataModel';

const { Panel } = Collapse;
const { Text } = Typography;

const DAYS = 28;
const PANEL_KEY = 'health';
// Soft translucent panel, matching the "Usage — Last 4 Weeks" chart container
// in this modal (filled background instead of a hard outline).
const PANEL_BG: React.CSSProperties = {
  background: 'var(--soft-panel-bg)',
  border: 'var(--soft-panel-border)', boxShadow: 'var(--soft-panel-shadow)',
  borderRadius: 8,
  padding: 12,
};

// Fixed display order; the six known metrics always get a slot (so weight,
// sleep and blood oxygen always appear, even before the user has data for them).
const METRIC_ORDER = ['steps', 'activeEnergy', 'distanceWalking', 'exerciseTime', 'restingHeartRate', 'hrv', 'oxygenSaturation', 'bodyMass', 'sleep'];

// Last-28-days category axis, matching the cushion usage chart in this modal.
const build28Days = () => {
  const labels: string[] = [];
  const keys: string[] = [];
  for (let i = DAYS - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    keys.push(d.toISOString().slice(0, 10)); // YYYY-MM-DD (matches day-bucket `t`)
    labels.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
  }
  return { labels, keys };
};

// Map a series' points (keyed by day) onto the 28-day axis (null where missing).
const seriesValues = (series: HealthMetricSeries | undefined, keys: string[]): (number | null)[] => {
  if (!series) return keys.map(() => null);
  const byDay = new Map(series.points.map((p) => [p.t.slice(0, 10), p.value]));
  return keys.map((k) => (byDay.has(k) ? (byDay.get(k) as number) : null));
};

const fmtVal = (v: number, unit: string | null) =>
  `${v.toLocaleString()}${unit && unit !== 'count' ? ` ${unit}` : ''}`;

// Minutes → "8h 12m" (or "45m"), for sleep durations.
const fmtDuration = (mins: number) => {
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};

// 'Mon D' label from a day-bucket key (YYYY-MM-DD...), adding the year when the
// point isn't from the current year (e.g. an old weigh-in) so it's not confused
// with a recent date.
const fmtDayLabel = (t: string) => {
  const d = new Date(`${t.slice(0, 10)}T00:00:00`);
  const opts: Intl.DateTimeFormatOptions =
    d.getFullYear() !== new Date().getFullYear()
      ? { month: 'short', day: 'numeric', year: 'numeric' }
      : { month: 'short', day: 'numeric' };
  return d.toLocaleDateString('en-US', opts);
};

// Vertical gradient for bars (solid top → faded bottom) and a soft area fill
// under lines. `${c}33` etc. are 8-digit hex alpha suffixes.
const barGradient = (c: string) => ({
  type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
  colorStops: [{ offset: 0, color: c }, { offset: 1, color: `${c}40` }],
});
const areaGradient = (c: string) => ({
  type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
  colorStops: [{ offset: 0, color: `${c}66` }, { offset: 1, color: `${c}05` }],
});

const HealthDataSection = ({ userId, usageBreakdown, refreshNonce = 0 }: {
  userId: number;
  usageBreakdown: { date: string; minutes: number }[];
  refreshNonce?: number;
}): JSX.Element => {
  const theme = useTheme();
  const colors = getChartColors(theme);
  const textColor = theme.colors.text.main;

  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [data, setData] = useState<HealthAdminResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [overlayMetric, setOverlayMetric] = useState<string>('steps');

  const { labels, keys } = build28Days();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getUserHealthData(userId, { bucket: 'day' });
      setData(resp);
      const present = Object.keys(resp.metrics);
      if (present.length && !present.includes('steps')) setOverlayMetric(present[0]);
    } catch (e) {
      setError('Failed to load health data.');
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, [userId]);

  // Fetch only the first time the panel is expanded.
  const onChange = (activeKeys: string | string[]) => {
    const open = Array.isArray(activeKeys) ? activeKeys.includes(PANEL_KEY) : activeKeys === PANEL_KEY;
    if (open && !fetched && !loading) load();
  };

  // The modal's Refresh button bumps refreshNonce — re-fetch if this section was
  // already opened (even if since collapsed), so its data stays in sync. Skips
  // the initial mount and sections never opened.
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return; }
    if (fetched && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce]);

  const axisLabel = { fontSize: 9, color: theme.colors.text.light, interval: 5 };
  const metricAxisLabel = { fontSize: 9, color: theme.colors.text.light }; // auto-thinned

  // Per-metric charts plot their OWN returned points over a dynamic axis (not a
  // fixed 28-day window), so sparse/older readings — a single old weigh-in, an
  // occasional SpO2/sleep sample — actually render instead of falling outside a
  // recent window. Line metrics show markers so even one point is visible.
  const metricChartOption = (metric: string, series: HealthMetricSeries) => {
    const pts = [...series.points].sort((a, b) => a.t.localeCompare(b.t));
    const labels = pts.map((p) => fmtDayLabel(p.t));
    const values = pts.map((p) => p.value);
    const kind = metricKind(metric);
    const color = metricColor(metric);
    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const p = params[0];
          const head = `${metricLabel(metric)} · ${p.axisValue}`;
          if (p.value == null) return `${head}<br/>No data`;
          const body = metric === 'sleep' ? fmtDuration(p.value) : fmtVal(p.value, series.unit);
          const suffix = metric === 'sleep' ? ' asleep' : '';
          return `${head}<br/><b>${body}</b>${suffix}`;
        },
      },
      grid: { top: 8, bottom: 28, left: 4, right: 4 },
      xAxis: { type: 'category', data: labels, axisLine: { show: false }, axisTick: { show: false }, axisLabel: metricAxisLabel },
      yAxis: { type: 'value', show: false },
      series: [{
        type: kind,
        data: values,
        smooth: kind === 'line',
        showSymbol: kind === 'line',
        symbolSize: 6,
        barMaxWidth: 12,
        itemStyle: { borderRadius: 3, color: kind === 'bar' ? barGradient(color) : color },
        ...(kind === 'line' ? { lineStyle: { color, width: 2 }, areaStyle: { color: areaGradient(color) } } : {}),
      }],
    };
  };

  const combinedOption = () => {
    const sitting = new Map(usageBreakdown?.map((b) => [b.date, b.minutes]) ?? []);
    const sittingValues = keys.map((k) => (sitting.has(k) ? (sitting.get(k) as number) : 0));
    const series = data?.metrics[overlayMetric];
    const metricValues = seriesValues(series, keys);
    const label = metricLabel(overlayMetric);
    const mColor = metricColor(overlayMetric);

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const date = params?.[0]?.axisValue;
          const rows = params.map((pt: any) => {
            if (pt.value == null) return `${pt.marker} ${pt.seriesName}: —`;
            const isSitting = String(pt.seriesName).includes('Sitting');
            if (!isSitting && overlayMetric === 'sleep') {
              return `${pt.marker} ${pt.seriesName}: <b>${fmtDuration(Number(pt.value))}</b>`;
            }
            const suffix = isSitting ? ' min' : (series?.unit && series.unit !== 'count' ? ` ${series.unit}` : '');
            return `${pt.marker} ${pt.seriesName}: <b>${Number(pt.value).toLocaleString()}${suffix}</b>`;
          });
          return `${date}<br/>${rows.join('<br/>')}`;
        },
      },
      legend: { data: ['Sitting time (min)', label], top: 0, textStyle: { color: textColor, fontSize: 11 } },
      grid: { top: 32, bottom: 28, left: 40, right: 44 },
      xAxis: { type: 'category', data: labels, axisLine: { show: false }, axisTick: { show: false }, axisLabel },
      yAxis: [
        { type: 'value', name: 'min', nameTextStyle: { fontSize: 9, color: textColor }, axisLabel: { fontSize: 9, color: theme.colors.text.light } },
        { type: 'value', name: series?.unit || '', nameTextStyle: { fontSize: 9, color: textColor }, axisLabel: { fontSize: 9, color: theme.colors.text.light }, splitLine: { show: false } },
      ],
      series: [
        {
          name: 'Sitting time (min)', type: 'bar', data: sittingValues, yAxisIndex: 0,
          barMaxWidth: 12, itemStyle: { borderRadius: 3, color: barGradient(colors[1]) },
        },
        {
          name: label, type: 'line', data: metricValues, yAxisIndex: 1,
          smooth: true, showSymbol: false, connectNulls: true,
          lineStyle: { color: mColor, width: 2.5 }, itemStyle: { color: mColor },
          areaStyle: { color: areaGradient(mColor) },
        },
      ],
    };
  };

  const renderBody = () => {
    if (loading) return <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>;
    if (error) return <Empty description={error} />;
    if (!data || Object.keys(data.metrics).length === 0) {
      return <Empty description="No health data — user hasn't connected Apple Health / Health Connect or has no samples yet." />;
    }

    const present = Object.keys(data.metrics);
    // Known metrics in fixed order, then any extra metrics the backend returned.
    const orderedMetrics = [
      ...METRIC_ORDER,
      ...present.filter((m) => !METRIC_ORDER.includes(m)),
    ];

    return (
      <>
        {/* Combined correlation view */}
        <div style={{ ...PANEL_BG, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text strong>Cushion sitting time vs. health metric</Text>
            <Select size="small" value={overlayMetric} onChange={setOverlayMetric} style={{ minWidth: 150 }}>
              {present.map((m) => <Select.Option key={m} value={m}>{metricLabel(m)}</Select.Option>)}
            </Select>
          </div>
          <BaseChart option={combinedOption()} height="240px" />
        </div>

        {/* Per-metric charts, each in its own soft panel */}
        <Row gutter={[16, 16]}>
          {orderedMetrics.map((m) => {
            const series = data.metrics[m];
            const hasData = !!series && series.points.length > 0;
            return (
              <Col xs={24} md={12} key={m}>
                <div style={{ ...PANEL_BG, height: '100%' }}>
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>
                      {metricLabel(m)}
                      {series?.unit ? <Text type="secondary" style={{ fontWeight: 400 }}>{`  ·  ${series.unit}`}</Text> : null}
                    </Text>
                  </div>
                  {hasData ? (
                    <BaseChart option={metricChartOption(m, series)} height="150px" />
                  ) : (
                    <div style={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data yet" />
                    </div>
                  )}
                </div>
              </Col>
            );
          })}
        </Row>
      </>
    );
  };

  return (
    // Wrapped in the same soft translucent panel as the modal's other sections
    // so the collapsed row blends in; ghost Collapse removes AntD's own frame.
    <div style={{ ...PANEL_BG, padding: 0, overflow: 'hidden' }}>
      <Collapse ghost onChange={onChange} expandIconPosition="right">
        <Panel
          key={PANEL_KEY}
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#ff4d4f22',
              }}>
                <HeartOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>Health</div>
                <Text type="secondary" style={{ fontSize: 12 }}>Activity, heart, oxygen, weight & sleep — tap to expand</Text>
              </div>
            </div>
          }
        >
          {renderBody()}
        </Panel>
      </Collapse>
    </div>
  );
};

export default HealthDataSection;
